import React from 'react';
import { Box, Flex, VStack, Text, Container, HStack, Button, Divider, Heading, Icon } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaShoppingCart } from 'react-icons/fa';
import PageTitle from '../components/PageTitle';
import PaymentForm from '../components/PaymentForm';
import { useShoppingCartStore } from '../store/shoppingCartStore';
import GlassCard from '../components/GlassCard';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, removeItem, getTotalPrice } = useShoppingCartStore();
  const totalPrice = getTotalPrice();

  if (items.length === 0) {
    return (
      <Container maxW="7xl" pt={12}>
        <VStack spacing={6} align="stretch">
          <PageTitle mb={2} textAlign="left">Checkout</PageTitle>
          <GlassCard p={12} baseColor="#002e0b" textAlign="center">
            <VStack spacing={6}>
              <Icon as={FaShoppingCart} boxSize={16} color="gray.400" opacity={0.5} />
              <VStack spacing={2}>
                <Heading size="lg" color="white" mb={2}>
                  Your cart is empty
                </Heading>
                <Text color="gray.300" fontSize="md">
                  Add items to your cart to continue
                </Text>
              </VStack>
              <Button
                colorScheme="blue"
                size="lg"
                onClick={() => navigate('/store')}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                transition="all 0.2s"
              >
                Continue Shopping
              </Button>
            </VStack>
          </GlassCard>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="7xl" pt={12} pb={12}>
      <VStack spacing={8} align="stretch">
        <PageTitle mb={2} textAlign="left">Checkout</PageTitle>
        
        <Flex
          w="100%"
          gap={8}
          align="stretch"
          flexDirection={{ base: 'column', lg: 'row' }}
        >
          {/* Left Column: Cart Items */}
          <Box flex={1} minW={{ base: '100%', lg: '400px' }}>
            <GlassCard p={6} baseColor="#002e0b">
              <VStack align="stretch" spacing={6}>
                <Heading size="md" color="white" mb={2}>
                  Cart Items ({items.length})
                </Heading>
                <Divider borderColor="rgba(255, 255, 255, 0.1)" />
                
                <VStack
                  align="stretch"
                  spacing={4}
                  maxH="60vh"
                  overflowY="auto"
                  pr={2}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                      },
                    },
                  }}
                >
                  {items.map((cartItem) => (
                    <Box
                      key={cartItem.item.id}
                      p={4}
                      bg="rgba(0, 0, 0, 0.2)"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="rgba(255, 255, 255, 0.1)"
                      _hover={{
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        bg: 'rgba(0, 0, 0, 0.3)',
                        transform: 'translateY(-2px)',
                      }}
                      transition="all 0.2s"
                    >
                      <HStack justify="space-between" align="flex-start" spacing={4}>
                        <VStack align="flex-start" spacing={1} flex={1}>
                          <Text color="white" fontSize="md" fontWeight="medium">
                            {cartItem.item.name}
                          </Text>
                          {cartItem.item.description && (
                            <Text color="gray.400" fontSize="xs" noOfLines={2}>
                              {cartItem.item.description}
                            </Text>
                          )}
                          {cartItem.quantity > 1 && (
                            <Text color="gray.500" fontSize="xs">
                              Quantity: {cartItem.quantity}
                            </Text>
                          )}
                        </VStack>
                        <HStack spacing={4} align="center">
                          <VStack align="flex-end" spacing={0}>
                            <Text color="green.400" fontWeight="bold" fontSize="lg">
                              ${(cartItem.item.price * cartItem.quantity).toFixed(2)}
                            </Text>
                            {cartItem.quantity > 1 && (
                              <Text color="gray.500" fontSize="xs">
                                ${cartItem.item.price.toFixed(2)} each
                              </Text>
                            )}
                          </VStack>
                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeItem(cartItem.item.id)}
                            aria-label="Remove item"
                            _hover={{
                              bg: 'red.900',
                              transform: 'scale(1.1)',
                            }}
                            transition="all 0.2s"
                          >
                            <Icon as={FaTrash} />
                          </Button>
                        </HStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
                
                <Divider borderColor="rgba(255, 255, 255, 0.1)" />
                
                <Box
                  p={4}
                  bg="rgba(34, 197, 94, 0.1)"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="rgba(34, 197, 94, 0.3)"
                >
                  <HStack justify="space-between" align="center">
                    <Text fontWeight="bold" fontSize="xl" color="white">
                      Total:
                    </Text>
                    <Text fontWeight="bold" fontSize="2xl" color="green.400">
                      ${totalPrice.toFixed(2)}
                    </Text>
                  </HStack>
                </Box>
              </VStack> 
            </GlassCard>
          </Box>

          {/* Right Column: Payment Form */}
          <Box flex={1} minW={{ base: '100%', lg: '400px' }}>
            <GlassCard p={6} baseColor="#002e0b">
              <PaymentForm totalAmount={totalPrice} items={items} />
            </GlassCard>
          </Box>
        </Flex>
      </VStack>
    </Container>
  );
};

export default CheckoutPage; 